import React from 'react';
import { Transition } from 'react-transition-group';

type TransitionStates = 'entering' | 'entered' | 'exiting' | 'exited';

const DEFAULT_TIMEOUT = 300;

export interface Props {
  children: React.ReactElement;
  in: boolean;
  appear?: boolean;
  timeout?: number;
  delay?: number;
  unmountOnExit?: boolean;
  opacity?: number;
  onEnter?: (node: HTMLElement, isAppearing: boolean) => void;
  onEntered?: (node: HTMLElement, isAppearing: boolean) => void;
  onEntering?: (node: HTMLElement, isAppearing: boolean) => void;
  onExit?: (node: HTMLElement) => void;
  onExited?: (node: HTMLElement) => void;
  onExiting?: (node: HTMLElement) => void;
}

export const Fade: React.FC<Props> = (props: Props) => {
  const {
    children,
    appear = true,
    timeout = DEFAULT_TIMEOUT,
    in: inProp,
    unmountOnExit,
    delay,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    opacity = 1,
    ...other
  } = props;

  const styles = {
    entering: {
      opacity
    },
    entered: {
      opacity
    },
    exiting: {
      opacity: 0
    },
    exited: {
      opacity: 0
    }
  };

  const handleOnEnter = (node: HTMLElement, isAppearing: boolean) => {
    // Force the animation to start from the beginning or repaint. This fixes beginning animation for onMountOnExit
    // https://github.com/reactjs/react-transition-group/issues/159#issuecomment-322735285
    node.scrollTop;

    if (onEnter) {
      onEnter(node, isAppearing);
    }
  };

  const handleOnEntered = React.useCallback(
    (node: HTMLElement, isAppearing: boolean) => {
      if (onEntered) {
        onEntered(node, isAppearing);
      }
    },
    [onEntered]
  );

  const handleOnEntering = React.useCallback(
    (node: HTMLElement, isAppearing: boolean) => {
      if (onEntering) {
        onEntering(node, isAppearing);
      }
    },
    [onEntering]
  );

  const handleOnExit = React.useCallback(
    (node: HTMLElement) => {
      if (onExit) {
        onExit(node);
      }
    },
    [onExit]
  );

  const handleOnExited = React.useCallback(
    (node: HTMLElement) => {
      if (onExited) {
        onExited(node);
      }
    },
    [onExited]
  );

  const handleOnExiting = React.useCallback(
    (node: HTMLElement) => {
      if (onExiting) {
        onExiting(node);
      }
    },
    [onExiting]
  );

  return (
    <Transition
      timeout={timeout}
      in={inProp}
      appear={appear}
      unmountOnExit={unmountOnExit}
      onEnter={handleOnEnter}
      onEntered={handleOnEntered}
      onEntering={handleOnEntering}
      onExit={handleOnExit}
      onExited={handleOnExited}
      onExiting={handleOnExiting}
      {...other}
    >
      {/** @ts-expect-error - Need to be replaced with new api */}
      {(state: TransitionStates, childProps: { [key: string]: any }) => {
        return React.cloneElement(children, {
          style: {
            visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            transitionProperty: 'opacity',
            transitionDuration: `${timeout}ms`,
            transitionDelay: delay ? `${delay}ms` : undefined,
            ...styles[state],
            ...children.props.style
          },
          ...childProps
        });
      }}
    </Transition>
  );
};

export default React.memo(Fade);
